/**
 * The priced lines of an EW-5 field ticket or change order, office side.
 *
 * One table for both records. While the record takes edits (`editable`) each
 * line has Edit (description, quantity, rate) and Remove, and an Add line form
 * sits under the table; once it is signed, approved or void the table is read
 * only and no edit control is drawn at all. A line the record says is locked
 * (a change-order line on a ticket bills at the approved price) offers Remove
 * only.
 *
 * Every figure is the API's: amount, rate and the totals below are printed as
 * returned, never multiplied here. An unpriced line shows `—` and its reason.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { EwCatalog, EwMeta, EwTicketLineWrite } from '../../types/earthworks';
import { ewPriceUnits, ewRefusal, fmtMoney, fmtNumber } from '../../lib/earthworks';
import { EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError, EwPanel } from './EwBits';

export interface EwLineRow {
  id: string;
  kindLabel: string | null;
  kind: string;
  description: string;
  unitLabel: string | null;
  unit: string;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  unpriced?: boolean;
  unpricedReason?: string | null;
  note?: string | null;
  /** Remove only: the price is fixed elsewhere. */
  locked?: boolean;
}

const toNum = (s: string): number | undefined => {
  const t = s.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

export function EwLinesPanel({
  eyebrow,
  title,
  lines,
  currency,
  editable,
  catalog,
  meta,
  total,
  totalLabel,
  testId,
  onAdd,
  onUpdate,
  onRemove,
  pending,
}: {
  eyebrow: string;
  title: string;
  lines: EwLineRow[];
  currency: string | null;
  editable: boolean;
  catalog: EwCatalog | null;
  meta: EwMeta | null;
  /** The API's figure; `—` when absent. */
  total: number | null;
  totalLabel: string;
  testId: string;
  onAdd: (body: EwTicketLineWrite) => Promise<unknown>;
  onUpdate: (lineId: string, body: EwTicketLineWrite) => Promise<unknown>;
  onRemove: (lineId: string) => Promise<unknown>;
  pending: boolean;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [desc, setDesc] = useState('');
  const [qty, setQty] = useState('');
  const [rate, setRate] = useState('');
  const [rowError, setRowError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [add, setAdd] = useState({ kind: '', description: '', unit: '', quantity: '', rate: '' });
  const [addError, setAddError] = useState<string | null>(null);

  const startEdit = (l: EwLineRow) => {
    setRowError(null);
    setEditingId(l.id);
    setDesc(l.description);
    setQty(l.quantity === null ? '' : String(l.quantity));
    setRate(l.rate === null ? '' : String(l.rate));
  };

  const saveEdit = async (l: EwLineRow) => {
    setRowError(null);
    const body: EwTicketLineWrite = {};
    if (desc.trim() && desc.trim() !== l.description) body.description = desc.trim();
    const q = toNum(qty);
    if (q === undefined) return setRowError('Enter a quantity.');
    if (q !== l.quantity) body.quantity = q;
    const r = toNum(rate);
    if (rate.trim() !== '' && r === undefined) return setRowError('The rate must be a number.');
    if (r !== undefined && r !== l.rate) body.rate = r;
    if (Object.keys(body).length === 0) return setEditingId(null);
    try {
      await onUpdate(l.id, body);
      setEditingId(null);
      toast.success('Line saved');
    } catch (err) {
      setRowError(ewRefusal(err, 'The line could not be saved.'));
    }
  };

  const remove = async (l: EwLineRow) => {
    setRowError(null);
    try {
      await onRemove(l.id);
      toast.success('Line removed');
    } catch (err) {
      setRowError(ewRefusal(err, 'The line could not be removed.'));
    }
  };

  const units = ewPriceUnits(catalog?.priceUnits, meta);
  const submitAdd = async () => {
    setAddError(null);
    if (!add.kind) return setAddError('Pick what the line is for.');
    if (!add.description.trim()) return setAddError('Describe the line.');
    if (!add.unit) return setAddError('Pick a unit.');
    const q = toNum(add.quantity);
    if (q === undefined || q <= 0) return setAddError('Enter a quantity greater than 0.');
    const r = toNum(add.rate);
    if (r === undefined || r < 0) return setAddError('Enter the rate.');
    try {
      await onAdd({ kind: add.kind, description: add.description.trim(), unit: add.unit, quantity: q, rate: r });
      setAdd({ kind: '', description: '', unit: '', quantity: '', rate: '' });
      setAdding(false);
      toast.success('Line added');
    } catch (err) {
      setAddError(ewRefusal(err, 'The line could not be added.'));
    }
  };

  return (
    <EwPanel
      eyebrow={eyebrow}
      title={title}
      testId={testId}
      actions={
        editable && !adding ? (
          <button type="button" className={EW_SECONDARY} onClick={() => { setAddError(null); setAdding(true); }} data-testid={`${testId}-add`}>
            <Plus className="size-4" aria-hidden="true" />
            Add line
          </button>
        ) : undefined
      }
    >
      {rowError && <div className="px-4 pt-3"><EwFormError message={rowError} /></div>}
      {lines.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-fieldiq-text">No lines yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="text-left text-xs text-fieldiq-muted">
              <tr>
                <th className="px-4 py-2 font-bold">Description</th>
                <th className="px-3 py-2 font-bold">Kind</th>
                <th className="px-3 py-2 font-bold">Unit</th>
                <th className="px-3 py-2 text-right font-bold">Qty</th>
                <th className="px-3 py-2 text-right font-bold">Rate</th>
                <th className="px-3 py-2 text-right font-bold">Amount</th>
                {editable && <th className="px-4 py-2 text-right font-bold"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) =>
                editable && editingId === l.id ? (
                  <tr key={l.id} className="border-t border-fieldiq-border" data-testid={`${testId}-row-edit`}>
                    <td className="px-4 py-2"><input className={EW_INPUT} aria-label="Line description" value={desc} onChange={(e) => setDesc(e.target.value)} /></td>
                    <td className="px-3 py-2 text-fieldiq-muted">{l.kindLabel || l.kind || '—'}</td>
                    <td className="px-3 py-2 text-fieldiq-muted">{l.unitLabel || l.unit || '—'}</td>
                    <td className="px-3 py-2"><input className={`${EW_INPUT} text-right`} inputMode="decimal" aria-label="Line quantity" value={qty} onChange={(e) => setQty(e.target.value)} /></td>
                    <td className="px-3 py-2"><input className={`${EW_INPUT} text-right`} inputMode="decimal" aria-label="Line rate" value={rate} onChange={(e) => setRate(e.target.value)} disabled={l.locked} /></td>
                    <td className="px-3 py-2 text-right font-mono text-fieldiq-muted">{fmtMoney(l.amount, currency)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <button type="button" className={EW_SECONDARY} onClick={() => setEditingId(null)} disabled={pending}>Cancel</button>{' '}
                      <button type="button" className={EW_PRIMARY} onClick={() => saveEdit(l)} disabled={pending}>Save</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={l.id} className="border-t border-fieldiq-border text-fieldiq-text" data-testid={`${testId}-row`}>
                    <td className="px-4 py-2">
                      <span className="text-white">{l.description}</span>
                      {l.note && <span className="block text-xs text-fieldiq-muted">{l.note}</span>}
                      {l.unpriced && (
                        <span className="mt-0.5 block text-xs font-bold text-fi-amber" data-testid={`${testId}-unpriced`}>
                          No rate{l.unpricedReason ? ` — ${l.unpricedReason}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">{l.kindLabel || l.kind || '—'}</td>
                    <td className="px-3 py-2">{l.unitLabel || l.unit || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNumber(l.quantity)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(l.rate, currency)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(l.amount, currency)}</td>
                    {editable && (
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        {!l.locked && (
                          <button type="button" className={EW_SECONDARY} aria-label={`Edit ${l.description}`} onClick={() => startEdit(l)} disabled={pending}>
                            <Pencil className="size-4" aria-hidden="true" />
                          </button>
                        )}{' '}
                        <button type="button" className={EW_SECONDARY} aria-label={`Remove ${l.description}`} onClick={() => remove(l)} disabled={pending}>
                          <Trash2 className="size-4" aria-hidden="true" />
                        </button>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end border-t border-fieldiq-border px-4 py-3 text-sm">
        <span className="text-fieldiq-muted">{totalLabel}</span>
        <span className="ml-4 font-mono font-bold text-white" data-testid={`${testId}-total`}>{fmtMoney(total, currency)}</span>
      </div>
      {editable && adding && (
        <div className="space-y-3 border-t border-fieldiq-border px-4 py-4" data-testid={`${testId}-add-form`}>
          <EwFormError message={addError} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <EwField label="Kind">
              <select className={EW_INPUT} aria-label="New line kind" value={add.kind} onChange={(e) => setAdd({ ...add, kind: e.target.value })}>
                <option value="">Pick…</option>
                {(catalog?.priceKinds ?? []).map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </EwField>
            <EwField label="Description">
              <input className={EW_INPUT} aria-label="New line description" value={add.description} onChange={(e) => setAdd({ ...add, description: e.target.value })} />
            </EwField>
            <EwField label="Unit">
              <select className={EW_INPUT} aria-label="New line unit" value={add.unit} onChange={(e) => setAdd({ ...add, unit: e.target.value })}>
                <option value="">Pick…</option>
                {units.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </EwField>
            <EwField label="Quantity">
              <input className={EW_INPUT} inputMode="decimal" aria-label="New line quantity" value={add.quantity} onChange={(e) => setAdd({ ...add, quantity: e.target.value })} />
            </EwField>
            <EwField label={currency ? `Rate (${currency})` : 'Rate'}>
              <input className={EW_INPUT} inputMode="decimal" aria-label="New line rate" value={add.rate} onChange={(e) => setAdd({ ...add, rate: e.target.value })} />
            </EwField>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className={EW_SECONDARY} onClick={() => setAdding(false)} disabled={pending}>Cancel</button>
            <button type="button" className={EW_PRIMARY} onClick={submitAdd} disabled={pending}>Add line</button>
          </div>
        </div>
      )}
    </EwPanel>
  );
}

/** A file picker as a button, for an upload that starts on choose. */
export function EwFilePick({
  label,
  onPick,
  disabled,
  testId,
  accept = 'image/*,application/pdf',
}: {
  label: string;
  onPick: (file: File) => void;
  disabled?: boolean;
  testId?: string;
  accept?: string;
}) {
  const ref = React.useRef<HTMLInputElement | null>(null);
  return (
    <>
      <button type="button" className={EW_SECONDARY} onClick={() => ref.current?.click()} disabled={disabled}>
        {label}
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={testId}
        aria-label={label}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onPick(f);
        }}
      />
    </>
  );
}
